/**
 * Entry point: boot, then mount whatever the current state asks for.
 *
 * This file stays small on purpose. It knows how to start the app, who is
 * signed in, and which feature owns which route — nothing else. Features are
 * imported lazily so adding the eleventh does not slow down the first paint.
 */

import './styles.css'
import './features/forms/forms.css'
import './features/concierge/concierge.css'
import './features/dashboard/dashboard.css'
import './features/itinerary/notes-reader.css'
import './features/clients/clients.css'
import './features/leads/leads.css'
/* Note: `features/account/account.css` existed since 20 Aug and was imported
   by NOTHING — its rules were duplicated inside styles.css instead, which is
   why editing it changed nothing on screen. Imported properly from 22 Aug. */
import './features/account/account.css'
import './features/analytics/analytics.css'
import './features/shell/shell.css'
import './features/assistant/assistant.css'
import './styles/modal.css'
import './ui/command-palette.css'
import './features/settings/settings.css'
import './features/social/social.css'
import './styles/theme-slate.css'
import { initFirebase, onUser, consumeRedirectResult, tokenReady } from './lib/firebase.js'
import { setNamespace } from './lib/store.js'
import { restorePreferences } from './lib/restore-prefs.js'
import { setRouter } from './lib/navigate.js'
import { recoverFromStaleBundle } from './lib/stale-bundle.js'
import { internalTarget } from './lib/link-click.js'
import { h, replace, qs } from './lib/dom.js'
import { toast } from './lib/toast.js'
import { overDashboard } from './over-dashboard.js'
import { appUrl, isAppPath } from './lib/app-base.js'
import { startUsageSignals } from './lib/usage-signals.js'
import { attachCommandPalette } from './ui/command-palette.js'

/** The signed-in advisor, for panels this file opens on a feature's behalf. */
let signedInUid = ''

/** @type {(() => void)|null} */
let teardownCurrentView = null

/**
 * ── Client-side navigation ────────────────────────────────────────────────
 *
 * Every in-app link used to be a plain `<a href>`, so moving between screens
 * was a full page load: parse the bundle, initialise Firebase, wait on the
 * auth listener, re-read settings, re-subscribe to trips. Seconds of blank
 * screen between two screens of the same app.
 *
 * Intercepting the click keeps the tab alive — Firebase, the auth session and
 * the Firestore cache all stay warm, so a screen change costs a lazy import
 * and a render. Only `/v2` paths; `/app` is a different program.
 */

/**
 * Swap to another screen without reloading the page.
 * @param {HTMLElement|null} root
 * @param {URL} url
 */
async function navigate(root, url) {
  history.pushState({}, '', url.href)
  tearDown()
  await renderRoute(root)
  /* A new screen starts at the top. Without this the browser keeps the
     previous screen's scroll position on a page that no longer has it. */
  globalThis.scrollTo({ top: 0 })
}

/** @param {HTMLElement|null} root */
function startRouter(root) {
  document.addEventListener('click', (event) => {
    const url = internalTarget(/** @type {MouseEvent} */ (event))
    if (!url) return
    event.preventDefault()
    navigate(root, url).catch((err) => {
      if (!recoverFromStaleBundle(err)) console.error('navigation failed', err)
    })
  })

  globalThis.addEventListener('popstate', () => {
    tearDown()
    renderRoute(root).catch((err) => console.error('navigation failed', err))
  })

  /* The same swap, for the screens that navigate from a handler rather than
     from a link — opening a trip out of the dashboard grid, landing on a trip
     that was just duplicated. Without this they would call `location.assign`
     and pay for the bundle, the SDK and the auth round-trip again, which is
     the page load the router above exists to end. See `lib/navigate.js`. */
  setRouter((href) => {
    let url
    try {
      url = new URL(href, location.href)
    } catch {
      return
    }
    /* A feature asking for something outside /v2 — the legacy editor — is
       asking for a real navigation, and must get one. */
    if (url.origin !== location.origin || !isAppPath(url.pathname)) {
      location.assign(url.href)
      return
    }
    navigate(root, url).catch((err) => {
      if (!recoverFromStaleBundle(err)) console.error('navigation failed', err)
    })
  })
}

function tearDown() {
  if (teardownCurrentView) {
    try {
      teardownCurrentView()
    } catch (err) {
      console.error('view teardown failed', err)
    }
    teardownCurrentView = null
  }
}

/** @param {string} message */
function fatal(message) {
  replace(
    qs('[data-app-root]'),
    h(
      'div',
      { class: 'boot-error', role: 'alert' },
      h('h1', {}, 'MBM Studio could not start'),
      h('p', {}, message)
    )
  )
}

async function boot() {
  const root = qs('[data-app-root]')
  replace(root, h('div', { class: 'boot-splash' }, 'Loading…'))

  const result = await initFirebase()
  if (!result.ok) {
    fatal(result.reason || 'Unknown startup failure.')
    return
  }

  // If this page load is the return leg of a Google redirect sign-in, collect
  // it before the auth listener runs so we do not flash the sign-in screen.
  await consumeRedirectResult()

  onUser(async (user) => {
    setNamespace(user ? user.uid : '')
    signedInUid = user ? user.uid : ''
    attachCommandPalette(signedInUid) // ⌘K search; '' tears the old one down
    tearDown()

    if (!user) {
      const { mountAuthScreen } = await import('./features/auth/screen.js')
      teardownCurrentView = mountAuthScreen(root)
      return
    }

    /* Before ANY read. `onUser` reports an account as soon as one exists,
       which for one created a second ago is before its token works — and the
       reads below would all be refused. See `lib/watch-retry.js`. */
    await tokenReady(user)
    startUsageSignals() // sign-in, crashes and rage clicks — lib/usage-signals.js

    /* Theme, brand colours, view format, text size and navigation — every
       preference this advisor has ever set, put back before the first paint.
       `lib/restore-prefs.js` holds the four blocks that used to be here and
       the reasoning for the order they run in. */
    restorePreferences(user.uid)

    // Both live on the body and outlive every route change.
    import('./features/assistant/float.js')
      .then((m) => m.mountFloatingAssistant({ notify: toast }))
      .catch((err) => console.error('assistant mount failed', err))
    Promise.all([import('./features/settings/index.js'), import('./features/shell/index.js')])
      .then(([s, sh]) => sh.setSettingsOpener(s.mountSettings({ notify: toast }).open))
      .catch((err) => console.error('settings mount failed', err))

    /* Billing became a tab inside the Account modal on 28 Aug 2026, off the
       main menu. Wired HERE because a feature may not import another feature
       (ARCHITECTURE.md §3) and `features/account` would otherwise be reaching
       into `features/billing` — the same reason `setSettingsOpener` is wired
       on the line above. `account/billing-slot.js` holds the other end. */
    Promise.all([import('./features/account/billing-slot.js'), import('./features/billing/index.js')])
      .then(([slot, billing]) =>
        slot.setBillingMounter((host) => billing.mountBilling({ container: host, notify: toast }))
      )
      .catch((err) => console.error('billing tab wiring failed', err))

    await renderRoute(root)
  })

  /* After the first render, so a click cannot land mid-boot. */
  startRouter(root)
}

/* Wired here because a feature may not import another feature (§3), and
   dynamic so the post styles stay out of the bundle. @param {any} trip */
async function openSocialExport(trip) {
  const { openSocialExport: open } = await import('./features/social/index.js')
  open(trip, { uid: signedInUid, notify: toast })
}

/** @param {HTMLElement|null} root */
async function renderRoute(root) {
  try {
    return await routeTo(root)
  } catch (err) {
    /* A screen is a dynamic import, and a deploy deletes the chunk this page
       was holding the name of. Reload once and the new index.html names the
       new chunks. Anything else is re-thrown so it is not swallowed. */
    if (recoverFromStaleBundle(err)) return undefined
    throw err
  }
}

/** @param {HTMLElement|null} root */
async function routeTo(root) {
  const path = globalThis.location ? globalThis.location.pathname : '/'

  // Leads, Clients, Account, Form Builder and Concierge are ported. Everything
  // else still lives in the legacy app, served from /app — ARCHITECTURE.md §6.
  /* New Leads, Clients and My Account are each a card floating over the
     dashboard, the way the original has them (`_ORIGINAL-app.html:4229`,
     `4620`, `4640`). `overDashboard` is the composition; `bareBar` because the
     bar underneath is the dashboard's. */
  /* ── Leads AND Clients, one screen with two tabs ──────────────────────
     Trey, 28 Aug 2026: a lead is a client who has not booked yet, and they
     were two menu rows. Both PATHS still route — the Concierge's "convert
     this lead" link, the dashboard's leads button and every bookmark keep
     working — and the path chooses the opening tab (`people/screen.js`).

     The two screens are registered as MOUNTERS rather than imported by the
     People screen: it is a third feature, and a feature may not import a
     feature (ARCHITECTURE.md §3). Registered here, on the route that needs
     them, so neither is loaded until an advisor asks for people at all. */
  /* Leads and Clients are one screen with two tabs since 28 Aug 2026, and
     wiring three features together needs more than the four lines every other
     route here takes — see `people-route.js`. Both paths still route, and the
     path chooses the opening tab. */
  /* Order matters and is asserted: `shell-routes.js` reads these `includes`
     calls straight out of this file and pins them against `ROUTES`, so the
     menu can never disagree with what actually mounts. */
  if (path.includes('/leads') || path.includes('/clients')) {
    const { mountShell } = await import('./features/shell/index.js')
    const { mountPeopleRoute } = await import('./people-route.js')
    teardownCurrentView = await mountPeopleRoute(root, path, toast, mountShell)
    return
  }

  if (path.includes('/account')) {
    const { mountShell } = await import('./features/shell/index.js')
    const { mountAccountScreen } = await import('./features/account/screen.js')
    const { mountDashboardScreen } = await import('./features/dashboard/screen.js')
    const mount = overDashboard(mountDashboardScreen, mountAccountScreen, toast)
    teardownCurrentView = mountShell(root, { path, bareBar: true, mount })
    return
  }

  if (path.includes('/forms')) {
    const { mountShell } = await import('./features/shell/index.js')
    const { mountFormsScreen } = await import('./features/forms/screen.js')
    teardownCurrentView = mountShell(root, { path, mount: (host) => mountFormsScreen(host) })
    return
  }

  if (path.includes('/concierge')) {
    const { mountShell } = await import('./features/shell/index.js')
    const { mountConciergeScreen } = await import('./features/concierge/screen.js')
    // The desk names itself, so the shell's bar is bare and unnamed.
    const o = { bareBar: true, barName: false, mount: (x) => mountConciergeScreen(x) }
    teardownCurrentView = mountShell(root, { path, ...o })
    return
  }

  if (path.includes('/dashboard')) {
    const { mountShell } = await import('./features/shell/index.js')
    const { mountDashboardScreen } = await import('./features/dashboard/screen.js')
    teardownCurrentView = mountShell(root, {
      path,
      bareBar: true, // no bar on the dashboard — shell-view.js#renderBar
      mount: (host) =>
        mountDashboardScreen(host, {
          notify: toast,
        }),
    })
    return
  }

  if (path.includes('/itinerary')) {
    const { mountShell } = await import('./features/shell/index.js')
    const { mountItineraryScreen } = await import('./features/itinerary/screen.js')
    teardownCurrentView = mountShell(root, {
      path,
      /* The editor raises the intent; this file is the only place that knows
         both the editor and the social panel exist. */
      mount: (host) => mountItineraryScreen(host, { onSocialExport: openSocialExport }),
    })
    return
  }

  if (path.includes('/billing')) {
    const { mountShell } = await import('./features/shell/index.js')
    const { mountBillingScreen } = await import('./features/billing/screen.js')
    teardownCurrentView = mountShell(root, {
      path,
      mount: (host) =>
        mountBillingScreen(host, {
          notify: toast,
        }),
    })
    return
  }

  if (path.includes('/import')) {
    const { mountShell } = await import('./features/shell/index.js')
    const { mountImportScreen } = await import('./features/import/screen.js')
    teardownCurrentView = mountShell(root, {
      path,
      mount: (host) =>
        mountImportScreen(host, {
          notify: toast,
        }),
    })
    return
  }

  if (path.includes('/cards')) {
    const { mountShell } = await import('./features/shell/index.js')
    const { mountCardsScreen } = await import('./features/cards/screen.js')
    teardownCurrentView = mountShell(root, {
      path,
      mount: (host) =>
        mountCardsScreen(host, {
          notify: toast,
        }),
    })
    return
  }

  if (path.includes('/analytics')) {
    const { mountShell } = await import('./features/shell/index.js')
    const { mountAnalyticsScreen } = await import('./features/analytics/screen.js')
    teardownCurrentView = mountShell(root, {
      path,
      mount: (host) =>
        mountAnalyticsScreen(host, {
          notify: toast,
        }),
    })
    return
  }

  if (path.includes('/resources')) {
    const { mountShell } = await import('./features/shell/index.js')
    const { mountResourcesScreen } = await import('./features/resources/screen.js')
    teardownCurrentView = mountShell(root, { path, mount: (host) => mountResourcesScreen(host) })
    return
  }

  /* ── /v2 is the dashboard ────────────────────────────────────────────────
   * This used to be a hand-written list of links — "MBM Studio v2 / This is
   * the rebuilt app. Eight features have been ported so far" — written when
   * there was no ported home screen to land on. The dashboard shipped on
   * 16 Aug and nothing came back to remove the placeholder, so every "Back"
   * link and the shell's own brand link, which both point at /v2, dumped the
   * advisor on a page of raw links instead of taking them home.
   *
   * It carried a second problem: it was built from `.shell`/`.shell-body`,
   * a whole separate chrome from the `.shell2-` one every other screen uses,
   * so it had no menu, no connection banner, and its own sign-out button.
   *
   * The shell is given '/v2/dashboard' rather than the real path so the menu
   * marks Dashboard as current. The URL stays /v2 — no redirect, so no extra
   * history entry for Back to fight through.
   */
  const { mountShell } = await import('./features/shell/index.js')
  const { mountDashboardScreen } = await import('./features/dashboard/screen.js')
  teardownCurrentView = mountShell(root, {
    path: appUrl('/dashboard'),
    bareBar: true,
    mount: (host) =>
      mountDashboardScreen(host, { notify: toast }),
  })
}

boot().catch((err) => {
  console.error('boot failed', err)
  fatal('Something went wrong while starting. Please reload.')
})
